import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { Draggable, type DraggableProvided } from 'react-beautiful-dnd';
import { useIntl } from 'react-intl';

import { FlexColumn } from 'ui-components/components/flex';
import { NumberInput } from 'ui-components/components/number-input';
import { InputAdornmentPosition } from 'ui-components/components/number-input/input-adornment-position';
import { UnitAdornment } from 'ui-components/components/number-input/unit-adornment';
import { Text, TextType } from 'ui-components/components/text';

import { UnitPosition } from '../../../../../enums/units/unit-position';
import { DRAGGABLE_ID_SEPARATOR } from '../../constants/drag-drop';
import { messages } from './item-card-messages';
import style from './item-card.module.css';

import type { InputAdornmentPositionValues } from 'ui-components/components/number-input';
import type { OrderItem } from '../../types';

type ItemCardProps = {
  chunkId: string;
  index: number;
  item: OrderItem;
  leftAction?: ReactNode;
  maxQuantity: number;
  onQuantityChange?: (itemId: string, chunkId: string, newQuantity: number) => void;
  quantity: number;
  rightAction?: ReactNode;
};

export const ItemCard = ({ item, chunkId, index, quantity, maxQuantity, onQuantityChange, leftAction, rightAction }: ItemCardProps) => {
  const { formatMessage } = useIntl();

  const itemWeight = item.weight ? `${item.weight.value} ${item.weight.unit}` : null;

  const handleQuantityChange = useCallback(
    (newQuantity: number | undefined) => {
      if (newQuantity !== undefined) {
        onQuantityChange?.(item.fulfillmentPlanItemId, chunkId, newQuantity);
      }
    },
    [onQuantityChange, item.fulfillmentPlanItemId, chunkId],
  );

  return (
    <Draggable draggableId={`${item.fulfillmentPlanItemId}${DRAGGABLE_ID_SEPARATOR}${chunkId}`} index={index}>
      {(provided: DraggableProvided) => (
        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className={style['item-card']}>
          {leftAction}

          <FlexColumn className={style['item-info']}>
            <Text className={style['item-name']} textType={TextType.Paragraph} value={item.name} />
            <Text
              className={style['item-sku']}
              textType={TextType.Caption}
              value={messages.itemSkuLabel}
              formattedMessageValues={{ sku: item.sku || '' }}
            />
            {itemWeight && <Text className={style['item-weight']} textType={TextType.Caption} value={itemWeight} />}
          </FlexColumn>

          <NumberInput
            value={quantity}
            onChange={handleQuantityChange}
            min={1}
            max={maxQuantity}
            className={style['quantity-input']}
            adornment={<UnitAdornment unitLabel={formatMessage(messages.unitLabel)} unitPosition={UnitPosition.InsideStart} />}
            adornmentPosition={InputAdornmentPosition.Start as InputAdornmentPositionValues}
          />
          {rightAction}
        </div>
      )}
    </Draggable>
  );
};
